import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  type LucideIcon,
  AlertTriangle,
  Archive,
  ChevronLeft,
  ChevronRight,
  Plus,
  Users,
} from "lucide-react";
import { motion } from "motion/react";
import { useState, useTransition } from "react";

import {
  CoachEngagementCard,
  type CoachEngagementCardData,
} from "~/components/engagement/engagement-card-coach";
import { CoachCardSkeletonGrid } from "~/components/engagement/engagement-card-skeleton";
import { CoachEngagementEmptyState } from "~/components/engagement/engagement-empty-state";
import { EngagementStatsBar } from "~/components/engagement/engagement-stats-bar";
import { ProgramTemplatesDialog } from "~/components/engagement/program-templates-dialog";
import { UnlockGoalsBar } from "~/components/engagement/unlock-goals-bar";
import { Button, ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { PageHeader } from "~/components/ui/page-title";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import { cn } from "~/lib/cn";
import { useEncryptionGate } from "~/lib/crypto";
import {
  clientProgramSummaryQueryOptions,
  useAllEngagementsByCoach,
  useClientProgramSummary,
} from "~/lib/queries/engagementQuery";
import { helpVideosQueryOptions } from "~/lib/queries/learningQuery";
import {
  hasGoalNeedingPin,
  initialClientProgramTab,
  lastClientProgramPage,
  parseClientProgramTab,
  sortForTab,
  statusesForTab,
  type ClientProgramTab,
} from "~/lib/shared/client-programs";

export const Route = createFileRoute("/_user/my-coaching/clients/programs/")({
  // The tab lives in the address so a reload, a shared link and the back arrow
  // from a program keep it. A missing or unknown value means "not chosen yet".
  // The router keeps a search value that fails validation unless the result
  // names the key, so an unknown tab is overwritten with `undefined`.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: ClientProgramTab | undefined } => ({
    tab: parseClientProgramTab(search.tab),
  }),
  // Without a tab, open on the first tab worth showing and write it in,
  // replacing this history entry so back still leaves the page. This runs
  // before the page renders, so it cannot override a navigation that already
  // left it. If the counts fail to load, the page shows that error itself.
  beforeLoad: async ({ search, context }) => {
    if (search.tab) return;
    const summary = await context.queryClient
      .fetchQuery(clientProgramSummaryQueryOptions())
      .catch(() => null);
    if (!summary) return;
    throw redirect({
      to: "/my-coaching/clients/programs",
      search: { tab: initialClientProgramTab(summary.counts) },
      replace: true,
    });
  },
  // The "How this works" button; a failed read only leaves it out.
  loader: async ({ context }) => {
    await context.queryClient
      .ensureQueryData(helpVideosQueryOptions())
      .catch(() => undefined);
  },
  component: ClientPrograms,
  staticData: { title: "Client programs" },
});

const PAGE_SIZE = 12;

const TABS = [
  { value: "current", label: "Current" },
  { value: "past", label: "Past" },
  { value: "all", label: "All" },
] as const satisfies readonly { value: ClientProgramTab; label: string }[];

const EMPTY_TAB_COPY: Record<
  ClientProgramTab,
  {
    icon: LucideIcon;
    title: string;
    description: string;
    /** The tab to offer instead, when the coach has programs there. */
    alternative?: Exclude<ClientProgramTab, "all">;
  }
> = {
  current: {
    icon: Users,
    title: "No current programs",
    description: "Active and paused programs show here.",
    alternative: "past",
  },
  past: {
    icon: Archive,
    title: "No past programs",
    description: "Programs move here when you finish, archive, or cancel them.",
    alternative: "current",
  },
  all: {
    icon: Users,
    title: "No programs",
    description: "Your coaching programs show here.",
  },
};

function ClientPrograms() {
  const coach = useCoachPrivateProfileRequired();
  const canCreateEngagements =
    coach.stripeChargesEnabled && coach.contractSigned;

  const { tab: tabInAddress } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [pageInTab, setPageInTab] = useState<{
    tab: ClientProgramTab;
    page: number;
  } | null>(null);
  const [isPending, startTransition] = useTransition();
  const encryption = useEncryptionGate();

  const { data: summary, error: summaryError } = useClientProgramSummary();
  // The route writes the default tab into the address before this renders. It
  // only stays empty when the counts failed to load there; then the first
  // successful load picks it, once, so refetches cannot move the coach.
  const [fallbackTab, setFallbackTab] = useState<ClientProgramTab | null>(null);
  if (fallbackTab === null && summary) {
    setFallbackTab(initialClientProgramTab(summary.counts));
  }
  const tab = tabInAddress ?? fallbackTab;

  // A page belongs to the tab it was chosen on: leaving the tab (by a click or
  // the browser's back arrow) forgets it, so coming back starts at page 1.
  if (pageInTab && pageInTab.tab !== tab) setPageInTab(null);
  const currentPage = pageInTab?.tab === tab ? pageInTab.page : 1;
  const setCurrentPage = (page: number) => {
    if (tab) setPageInTab({ tab, page });
  };

  const {
    data: engagementData,
    isPlaceholderData,
    error: listError,
  } = useAllEngagementsByCoach(
    currentPage,
    PAGE_SIZE,
    tab ? statusesForTab(tab) : undefined,
    tab ? sortForTab(tab) : undefined,
    { enabled: tab !== null },
  );

  const selectTab = (value: string) => {
    const nextTab = parseClientProgramTab(value);
    if (!nextTab) return;
    void navigate({ search: { tab: nextTab } });
  };

  const cardEngagements = (engagementData?.data ?? []).map(
    (e): CoachEngagementCardData => ({
      engagementId: e.engagementId,
      client: { name: e.client.name, avatar: e.client.avatar ?? null },
      statusChip: e.status,
      mainCoachingGoal: e.mainCoachingGoal ?? null,
      encryptedMainGoal: e.encryptedMainGoal ?? null,
      lastSessionSummary: e.lastSessionSummary ?? null,
      nextSession: e.nextSession ?? null,
      renewal: { renewalDueDate: e.renewalDate ?? null },
      startDate: e.startDate ?? null,
      progress: e.progress,
      totalInvoiceAmount: e.totalInvoiceAmount ?? 0,
      totalPaidAmount: e.totalPaidAmount ?? 0,
    }),
  );

  const error = summaryError ?? listError;
  const hasPrograms = !error && summary !== undefined && summary.counts.all > 0;
  const hasNoPrograms = !error && summary?.counts.all === 0;
  const isInitialLoading =
    !error && (!summary || (hasPrograms && !engagementData));
  const emptyTab = tab ? EMPTY_TAB_COPY[tab] : null;
  const totalPages = engagementData
    ? Math.ceil(engagementData.pagination.total / PAGE_SIZE)
    : 0;
  // A refetch can shrink the tab below the page the coach is on.
  if (engagementData && !isPlaceholderData) {
    const lastPage = lastClientProgramPage(
      engagementData.pagination.total,
      PAGE_SIZE,
    );
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }

  const createProgramLabel = (
    <>
      <Plus className="size-4" />
      Create new program
    </>
  );

  return (
    <div className="space-y-8">
      <PageHeader
        help="help_client_programs"
        title="Client programs"
        actions={
          canCreateEngagements ? (
            <>
              <ProgramTemplatesDialog />
              <ButtonRouter
                to="/my-coaching/clients/programs/new"
                variant="primary"
                size="sm"
                className="gap-2"
              >
                {createProgramLabel}
              </ButtonRouter>
            </>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled
                      className="gap-2"
                    >
                      {createProgramLabel}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-56">
                  Complete your payment setup to start creating programs.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        }
      />

      {!canCreateEngagements && (
        <section className="space-y-4">
          <p className="text-sm font-medium text-gray-900">
            Complete these steps to create coaching programs:
          </p>
          <ul className="space-y-2">
            {!coach.stripeChargesEnabled && (
              <li className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <AlertTriangle className="size-4 shrink-0 text-amber-800" />
                <div className="flex-1 space-y-1">
                  <p className="text-base font-medium text-gray-900">
                    Connect Stripe
                  </p>
                  <p className="text-text-secondary text-sm">
                    Connect your Stripe account to accept payments and create
                    coaching programs
                  </p>
                </div>
                <ButtonRouter to="/my-coaching/stripe-connect" size="sm">
                  Set up
                </ButtonRouter>
              </li>
            )}
            {!coach.contractSigned && (
              <li className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <AlertTriangle className="size-4 shrink-0 text-amber-800" />
                <div className="flex-1 space-y-1">
                  <p className="text-base font-medium text-gray-900">
                    Sign your coach agreement
                  </p>
                  <p className="text-text-secondary text-sm">
                    Sign the coach agreement before creating coaching programs
                  </p>
                </div>
              </li>
            )}
          </ul>
        </section>
      )}

      {isInitialLoading && <CoachCardSkeletonGrid />}

      {error && (
        <Card className="py-12">
          <EmptyState
            icon={AlertTriangle}
            title="We couldn't load your programs"
            description="Something went wrong on our end. Please refresh and try again."
          />
        </Card>
      )}

      {summary && hasPrograms && engagementData && (
        <>
          <EngagementStatsBar stats={summary.stats} />
          <section className="space-y-4">
            {tab && (
              <Tabs value={tab} onValueChange={selectTab}>
                <TabsList aria-label="Show programs">
                  {TABS.map(({ value, label }) => (
                    <TabsTrigger key={value} value={value} className="gap-1.5">
                      {label}
                      <span className="text-text-tertiary text-xs tabular-nums">
                        {summary.counts[value]}
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            <UnlockGoalsBar
              status={encryption.status}
              lockedUntil={encryption.lockedUntil}
              onUnlocked={encryption.refresh}
              hasGoalNeedingPin={hasGoalNeedingPin(cardEngagements)}
            />
            <div
              aria-busy={isPlaceholderData}
              className={cn(
                "grid grid-cols-1 gap-4 transition-opacity sm:grid-cols-2 lg:grid-cols-3",
                isPlaceholderData && "opacity-60",
              )}
            >
              {cardEngagements.map((engagement, index) => (
                <motion.div
                  key={engagement.engagementId}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: Math.min(index * 0.04, 0.3),
                    ease: "easeOut",
                  }}
                >
                  <CoachEngagementCard
                    engagement={engagement}
                    keyStatus={encryption.status}
                  />
                </motion.div>
              ))}
            </div>
            {cardEngagements.length === 0 && !isPlaceholderData && emptyTab && (
              <EmptyState
                icon={emptyTab.icon}
                size="sm"
                title={emptyTab.title}
                description={emptyTab.description}
                className="py-12"
              >
                {emptyTab.alternative &&
                  summary.counts[emptyTab.alternative] > 0 && (
                    <ButtonRouter
                      to="/my-coaching/clients/programs"
                      search={{ tab: emptyTab.alternative }}
                      variant="link"
                      size="sm"
                    >
                      View {emptyTab.alternative} programs (
                      {summary.counts[emptyTab.alternative]})
                    </ButtonRouter>
                  )}
              </EmptyState>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-text-secondary text-sm tabular-nums">
                  {engagementData.pagination.total} programs
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={currentPage <= 1 || isPending}
                    onClick={() =>
                      startTransition(() => setCurrentPage(currentPage - 1))
                    }
                  >
                    <ChevronLeft className="size-4" />
                    Previous
                  </Button>
                  <span className="text-text-secondary px-2 text-sm tabular-nums">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={
                      !engagementData.pagination.hasNextPage || isPending
                    }
                    onClick={() =>
                      startTransition(() => setCurrentPage(currentPage + 1))
                    }
                  >
                    Next
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {hasNoPrograms && canCreateEngagements && (
        <CoachEngagementEmptyState>
          <ButtonRouter
            to="/my-coaching/clients/programs/new"
            variant="primary"
            size="sm"
            className="gap-2"
          >
            {createProgramLabel}
          </ButtonRouter>
        </CoachEngagementEmptyState>
      )}
    </div>
  );
}
